import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, useFormikContext, getIn } from 'formik';
import Grid from '@material-ui/core/Grid';
import { Button } from '@material-ui/core';
import { brandConfig } from 'brand-config';
import { v4 as uuidv4 } from 'uuid';

import Field from 'core/components/form/form.v2';
import { Autocomplete } from 'core/components/autocomplete';
import { TextField } from 'core/components/text-field';
import { Tooltip } from 'core/components/tooltip';
import { useRouteHelper } from 'core/hooks/use-route-helper';
import { useStore } from 'core/store';
import { SubRiskCollapsibleSection } from 'common/components/farm/locations/risks/sub-risks/sub-risk-collapsible-section';
import FormSectionWrapper from 'common/components/form-section-wrapper';
import CollapsibleListItem from 'common/components/farm/locations/risks/sub-risks/collapsible-list-item';
import caretOrange from 'core/assets/svg/ic-orange-caret-thick.svg';
import {
  buildPropertyOptions,
  getItemDisplayText,
  navigateToLocationOrRisk,
  buildNotOnPolicyDetailFields,
  buildItemLinkId
} from '../helpers';
import useStyles from '../liability-coverages-styles';
import NotOnPolicyLocation, { locationInitialValues, locationValidationSchema } from './not-on-policy-location';
import NotOnPolicyStructure, { structureInitialValues, structureValidationSchema } from './not-on-policy-structure';
import NotOnPolicyResidence, { residenceInitialValues, residenceValidationSchema } from './not-on-policy-residence';

const FIELD_NAME = 'farmLiabilityCoverage.additionalPropertiesExtensions';
const NOT_ON_POLICY_ID = 'notOnPolicy';

const COVERAGE_OPTIONS = {
  location: [{ value: 'locationRentedToOthers', label: 'Location rented to others' }],
  structure: [{ value: 'structureRentedToOthers', label: 'Structure rented to others' }],
  residence: [
    { value: 'residenceRentedToOthers', label: 'Residence rented to others' },
    { value: 'residenceOccupiedByInsured', label: 'Residence occupied by insured' }
  ]
};

const ALL_COVERAGE_OPTIONS = Object.values(COVERAGE_OPTIONS).flat();
const getCoverageLabel = (value) => ALL_COVERAGE_OPTIONS.find((o) => o.value === value)?.label || value;

const NOT_ON_POLICY_TYPE_OPTIONS = [
  { id: 'location', value: 'Location', configKey: 'locations' },
  { id: 'structure', value: 'Structure', configKey: 'structures' },
  { id: 'residence', value: 'Residence', configKey: 'residences' }
];

const INITIAL_VALUES_BY_TYPE = {
  location: locationInitialValues,
  structure: structureInitialValues,
  residence: residenceInitialValues
};

const VALIDATION_SCHEMAS_BY_TYPE = {
  location: locationValidationSchema,
  structure: structureValidationSchema,
  residence: residenceValidationSchema
};

const getInitialValuesForType = (type) => ({
  type,
  coverage: COVERAGE_OPTIONS[type]?.[0]?.value || '',
  ...(INITIAL_VALUES_BY_TYPE[type] || {})
});

const AdditionalPropertiesExtensions = ({ disabled = false, onChangeActive }) => {
  const classes = useStyles();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();

  const items = getIn(values, FIELD_NAME) || [];

  const liabilityType = getIn(values, 'farmLiabilityCoverage.type');
  const { inPolicyRoute } = useRouteHelper();
  const {
    account: {
      policies: {
        policy: { policy: policyData }
      }
    }
  } = useStore();
  const state = inPolicyRoute
    ? policyData?.state
    : getIn(values, 'correctedAddress.state') || getIn(values, 'farmLocations.0.address.state');
  const typeConfig = brandConfig.farm?.[state]?.additionalPropertiesExtensions?.[liabilityType] || {};
  const enabledTypes = ['locations', 'residences', 'structures'].filter((type) => typeConfig[type]);
  const enabledTypesLabel = enabledTypes.join(', ');

  const typeSelectOptions = NOT_ON_POLICY_TYPE_OPTIONS.filter((opt) => typeConfig[opt.configKey]);

  const propertyOptions = useMemo(
    () => buildPropertyOptions(values.farmLocations, typeConfig, state),
    [values.farmLocations, typeConfig, state]
  );

  const [inputValue, setInputValue] = useState('');
  const [selectedProperty, setSelectedProperty] = useState(null);

  const handleSelectChange = useCallback((_event, val) => {
    setInputValue(val?.text || '');
    setSelectedProperty(val);
  }, []);

  const handleInputChange = useCallback((event) => {
    setInputValue(event.target.value);
    setSelectedProperty(null);
  }, []);

  const handleBlur = useCallback(() => {
    if (!selectedProperty) {
      setInputValue('');
    }
  }, [selectedProperty]);

  const handleNotOnPolicySubmit = useCallback(
    (formValues) => {
      const newItem = {
        id: uuidv4(),
        type: formValues.type,
        locationId: null,
        riskId: null,
        coverage: formValues.coverage
      };

      if (formValues.type === 'location') {
        const legalDescriptionFormat = formValues.legalDescription?.format;
        const address = formValues.address || {};

        newItem.legalDescription = formValues.legalDescription;
        newItem.additionalLegalDescription = formValues.additionalLegalDescription || null;
        newItem.locationDirections = formValues.locationDirections || null;
        newItem.description = formValues.description || null;
        newItem.numberOfAcres = formValues.numberOfAcres != null ? parseInt(formValues.numberOfAcres, 10) : null;
        newItem.addressDetails = {
          address: legalDescriptionFormat === 'street' ? address.address || null : null,
          address2: legalDescriptionFormat === 'street' ? address.address2 || null : null,
          city: address.city || null,
          state: address.state || null,
          zip: address.zip || null,
          county: address.county || null
        };
      }

      if (formValues.type === 'structure') {
        newItem.description = formValues.description || null;
        newItem.structureDetail = formValues.structureDetail || null;
      }

      if (formValues.type === 'residence') {
        const address = formValues.address || {};
        newItem.description = formValues.description || null;
        newItem.numFamilies = formValues.numFamilies != null ? parseInt(formValues.numFamilies, 10) : null;
        newItem.addressDetails = {
          address: address.address || null,
          address2: address.address2 || null,
          city: address.city || null,
          state: address.state || null,
          zip: address.zip || null
        };
      }

      setFieldValue(FIELD_NAME, [...items, newItem]);
      setFieldTouched(FIELD_NAME);
      setSelectedProperty(null);
      setInputValue('');
      onChangeActive();
    },
    [items, setFieldValue, setFieldTouched, onChangeActive]
  );

  const addedIds = useMemo(
    () => new Set(items.filter((item) => item.riskId || item.locationId).map((item) => item.riskId || item.locationId)),
    [items]
  );

  const availableOptions = useMemo(
    () =>
      propertyOptions.filter(
        (option) => option.id === NOT_ON_POLICY_ID || !addedIds.has(option.riskId || option.locationId)
      ),
    [propertyOptions, addedIds]
  );

  const filteredOptions = inputValue
    ? availableOptions.filter((option) => {
        const text = option.text || '';
        return (
          text.toLowerCase().includes(inputValue.toLowerCase()) ||
          option.id?.toString().toLowerCase().includes(inputValue.toLowerCase())
        );
      })
    : availableOptions;

  const currentValue = selectedProperty || (inputValue ? { id: inputValue, text: inputValue } : null);
  const inputValueProp = inputValue ? { inputValue } : {};

  return (
    <div className={classes.sectionContainer}>
      <SubRiskCollapsibleSection
        type="secondary"
        content={
          <div className={classes.headerText}>Additional properties liability extensions ({enabledTypesLabel})</div>
        }
        initialOpen={items.length > 0}
      >
        <FormSectionWrapper
          sectionTitle="Scheduled items"
          totalLabel="items"
          totalCount={items.length}
          emptyStateTitle="There are no items added yet"
          emptyStateSubtitle="To add one select from below"
        >
          {items.map((item, index) => {
            const displayText = getItemDisplayText(item, values.farmLocations, state);
            const isLocation = item.type === 'location';
            const isOnPolicy = item.riskId || item.locationId;
            const isNotOnPolicy = !isOnPolicy;
            const detailFields = [];

            if (isNotOnPolicy) {
              detailFields.push(...buildNotOnPolicyDetailFields(item));
            } else {
              const linkId = buildItemLinkId(item, values.farmLocations);
              detailFields.push({
                name: 'details',
                label: 'Details',
                value: (
                  <span className={classes.goToLink} onClick={() => navigateToLocationOrRisk(linkId)}>
                    {isLocation ? 'Go to location' : 'Go to risk'}
                    <img src={caretOrange} alt="" className={classes.goToLinkCaret} />
                  </span>
                ),
                size: 12
              });
            }
            detailFields.push({
              name: 'coverage',
              label: 'Coverage',
              value: getCoverageLabel(item.coverage),
              size: 12
            });

            const rightSideValues = isNotOnPolicy
              ? [
                  {
                    label: '',
                    value: (
                      <span className={classes.onlyCoveredHere}>
                        Only covered here
                        <Tooltip
                          text="This item is covered under liability only and does not appear elsewhere on the policy."
                          smallIcon
                        />
                      </span>
                    )
                  }
                ]
              : [];

            return (
              <CollapsibleListItem
                key={`additional-property-${index}`}
                title={
                  <div className={classes.itemTitleWrapper}>
                    <div>{displayText}</div>
                    <div className={classes.itemTypeLabel}>{item.type.toUpperCase()}</div>
                  </div>
                }
                rightSideValues={rightSideValues}
                detailFields={detailFields}
                onDelete={() => {
                  const newItems = items.filter((_, i) => i !== index);
                  setFieldValue(FIELD_NAME, newItems);
                  setFieldTouched(FIELD_NAME);
                  onChangeActive();
                }}
                disabled={disabled}
                keyPrefix={`additional-property-${index}`}
              />
            );
          })}

          <div className={classes.addFormContainer}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  value={currentValue}
                  {...inputValueProp}
                  id="propertySearch"
                  name="propertySearch"
                  options={filteredOptions}
                  onSelection={handleSelectChange}
                  onBlur={handleBlur}
                  showDeleteIcon
                  showChevronIcon
                  classes={{ option: classes.autocompleteOption }}
                  renderOption={(option) => (
                    <span className={option.id === NOT_ON_POLICY_ID ? classes.notOnPolicyOption : undefined}>
                      {option.text}
                    </span>
                  )}
                  InputComponent={TextField}
                  inputProps={{
                    label: 'Property search',
                    mode: 'dark',
                    onChange: handleInputChange,
                    disabled,
                    helperText: 'Search for an item on the policy',
                    FormHelperTextProps: { className: classes.autocompleteHelperText }
                  }}
                />
              </Grid>

              {selectedProperty && selectedProperty.id === NOT_ON_POLICY_ID && (
                <Grid item xs={12}>
                  <Formik
                    initialValues={{ type: '' }}
                    validateOnChange={false}
                    validateOnBlur={false}
                    validate={(formValues) => {
                      const schema = VALIDATION_SCHEMAS_BY_TYPE[formValues.type];
                      if (!schema) {
                        return {};
                      }
                      try {
                        schema.validateSync(formValues, { abortEarly: false, context: formValues });
                        return {};
                      } catch (err) {
                        const errors = {};
                        err.inner.forEach((error) => {
                          if (error.path) {
                            errors[error.path] = error.message;
                          }
                        });
                        return errors;
                      }
                    }}
                    onSubmit={handleNotOnPolicySubmit}
                  >
                    {({ values: formValues, submitForm, resetForm }) => (
                      <Grid container spacing={2}>
                        <Field
                          xs={12}
                          type="select"
                          name="type"
                          mode="dark"
                          label="Type of item"
                          options={typeSelectOptions}
                          onChange={(value) => {
                            resetForm({ values: getInitialValuesForType(value) });
                          }}
                          disabled={disabled}
                          className={classes.notOnPolicyTypeSelect}
                        />
                        <Grid item xs={12} className={classes.notOnPolicyFieldsContainer}>
                          <Grid container spacing={2}>
                            {formValues.type === 'location' && <NotOnPolicyLocation disabled={disabled} />}
                            {formValues.type === 'structure' && <NotOnPolicyStructure disabled={disabled} />}
                            {formValues.type === 'residence' && <NotOnPolicyResidence disabled={disabled} />}
                          </Grid>
                        </Grid>
                        {formValues.type && (
                          <>
                            <Grid item xs={12}>
                              <div className={classes.coverageLabel}>Coverage</div>
                            </Grid>
                            <Field
                              xs={12}
                              type="radio"
                              id="coverage"
                              name="coverage"
                              mode="dark"
                              options={COVERAGE_OPTIONS[formValues.type] || []}
                              disabled={disabled}
                              labelClassName={classes.coverageRadioLabel}
                              optionsSideBySide={false}
                            />
                            <Grid container alignItems="center" className={classes.addButtonRow}>
                              <Button
                                type="button"
                                mode="big"
                                variant="contained"
                                color="secondary"
                                className={classes.addButton}
                                disabled={disabled}
                                onClick={submitForm}
                              >
                                Add
                              </Button>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    )}
                  </Formik>
                </Grid>
              )}

              {selectedProperty && selectedProperty.id !== NOT_ON_POLICY_ID && (
                <Grid item xs={12}>
                  <Formik
                    key={selectedProperty.id}
                    initialValues={{ coverage: COVERAGE_OPTIONS[selectedProperty.type]?.[0]?.value || '' }}
                    validateOnChange={false}
                    validateOnBlur={false}
                    onSubmit={(formValues) => {
                      const newItem = {
                        id: selectedProperty.riskId || selectedProperty.locationId,
                        type: selectedProperty.type,
                        locationId: selectedProperty.locationId,
                        riskId: selectedProperty.riskId,
                        coverage: formValues.coverage,
                        locationLinkId: selectedProperty.locationLinkId,
                        riskLinkId: selectedProperty.riskLinkId,
                        // Location-specific fields
                        ...(selectedProperty.type === 'location' && {
                          legalDescription: selectedProperty.legalDescription,
                          additionalLegalDescription: selectedProperty.additionalLegalDescription,
                          locationDirections: selectedProperty.locationDirections,
                          addressDetails: selectedProperty.addressDetails
                        }),
                        // Structure-specific fields
                        ...(selectedProperty.type === 'structure' && {
                          description: selectedProperty.description
                        }),
                        // Residence-specific fields
                        ...(selectedProperty.type === 'residence' && {
                          addressDetails: selectedProperty.addressDetails,
                          numFamilies: selectedProperty.numFamilies
                        })
                      };
                      setFieldValue(FIELD_NAME, [...items, newItem]);
                      setFieldTouched(FIELD_NAME);
                      setSelectedProperty(null);
                      setInputValue('');
                      onChangeActive();
                    }}
                  >
                    {({ submitForm }) => (
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <div className={classes.coverageLabel}>Coverage</div>
                        </Grid>
                        <Field
                          xs={12}
                          type="radio"
                          id="coverage"
                          name="coverage"
                          mode="dark"
                          options={COVERAGE_OPTIONS[selectedProperty.type] || []}
                          disabled={disabled}
                          labelClassName={classes.coverageRadioLabel}
                        />
                        <Grid container alignItems="center" className={classes.addButtonRow}>
                          <Button
                            type="button"
                            mode="big"
                            variant="contained"
                            color="secondary"
                            className={classes.addButton}
                            disabled={disabled}
                            onClick={submitForm}
                          >
                            Add
                          </Button>
                        </Grid>
                      </Grid>
                    )}
                  </Formik>
                </Grid>
              )}
            </Grid>
          </div>
        </FormSectionWrapper>
      </SubRiskCollapsibleSection>
    </div>
  );
};

AdditionalPropertiesExtensions.propTypes = {
  disabled: PropTypes.bool.isRequired,
  onChangeActive: PropTypes.func.isRequired
};

export default AdditionalPropertiesExtensions;
